import {
  combineLatestTyped,
  IList,
  mergeTyped,
  useDataStream,
  useEventAsStream,
  useStreamValue
} from 'annotation-tools/utils';
import { Range } from 'immutable';
import { useState } from 'react';
import { Observable } from 'rxjs';
import { debounceTime, distinctUntilChanged, map, mapTo } from 'rxjs/operators';
import {
  paginationDebounceTime,
  searchDebounceTime
} from '../../constants/debounce-time';
import { rowsPerPageDefault } from '../../constants/rows-per-page-options';
import {
  ListWithPromiseState,
  listWithPromiseStateInit
} from '../../global-state/database/common/list-with-status';
import { mapListLatest } from '../../global-state/database/common/map-latest';
import { useValueAsDataStream } from '../../utils/use-value-as-data-stream';
import { hasSearchString } from '../has-search-string';

export const useListFiltered = <T extends { name: string }>(
  list$: Observable<ListWithPromiseState<T>>
) => {
  const [searchString, onSearchStringChange] = useState<string>('');
  const searchString$ = useValueAsDataStream(searchString);

  const [pageChange$, onPageChange] = useEventAsStream<number>();

  const [rowsPerPage, setRowsPerPage] = useState<number>(rowsPerPageDefault);
  const rowsPerPage$ = useValueAsDataStream(rowsPerPage);

  const listFiltered$ = useDataStream<ListWithPromiseState<T>>(
    listWithPromiseStateInit<T>(),
    combineLatestTyped(
      list$,
      searchString$.pipe(debounceTime(searchDebounceTime))
    ).pipe(
      map(([list, searchStr]) =>
        mapListLatest(list, ls =>
          ls.filter(a => hasSearchString(a.name, searchStr))
        )
      )
    )
  );

  const listPaginated$ = useDataStream<ListWithPromiseState<IList<T>>>(
    listWithPromiseStateInit<IList<T>>(),
    combineLatestTyped(listFiltered$, rowsPerPage$).pipe(
      map(([list, _rowsPerPage]) =>
        mapListLatest(list, ls =>
          Range(0, Math.ceil(ls.size / _rowsPerPage))
            .map(pageIdx =>
              ls.slice(pageIdx * _rowsPerPage, (pageIdx + 1) * _rowsPerPage)
            )
            .toList()
        )
      )
    )
  );

  const page$ = useDataStream<number>(
    0,
    mergeTyped(
      pageChange$,
      rowsPerPage$.pipe(distinctUntilChanged(), mapTo(0)),
      listFiltered$.pipe(
        map(a => a.value.latest.size),
        distinctUntilChanged(),
        mapTo(0)
      )
    )
  );

  const listFilteredCurrentPage$ = useDataStream<ListWithPromiseState<T>>(
    listWithPromiseStateInit<T>(),
    combineLatestTyped(
      listPaginated$,
      page$.pipe(debounceTime(paginationDebounceTime))
    ).pipe(
      map(([listlist, _page]) =>
        mapListLatest(listlist, ll => ll.get(_page) ?? IList<T>())
      )
    )
  );

  const page = useStreamValue(page$, 0);

  const listFiltered = useStreamValue(listFiltered$);
  const numRows = listFiltered?.value.latest.size ?? 0;
  const listFilteredCurrentPage = useStreamValue(listFilteredCurrentPage$);

  return {
    searchString,
    onSearchStringChange,
    page,
    onPageChange,
    rowsPerPage,
    setRowsPerPage,
    numRows,
    listFilteredCurrentPage
  };
};
